import { required, useGetList, useRecordContext } from "ra-core";
import { ReferenceInput } from "@/components/admin/reference-input";
import { TextInput } from "@/components/admin/text-input";
import { SelectInput } from "@/components/admin/select-input";
import { ArrayInput } from "@/components/admin/array-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { DateInput } from "@/components/admin/date-input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/use-mobile";

import ImageEditorField from "../misc/ImageEditorField";
import { isLinkedinUrl } from "../misc/isLinkedInUrl";
import type { Company, Entity, Sale } from "../types";
import { sizes } from "./sizes";

const isUrl = (url: string) => {
  if (!url) return;
  const UrlRegex = new RegExp(
    /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i,
  );
  if (!UrlRegex.test(url)) {
    return "Must be a valid URL";
  }
};

export const CompanyInputs = () => {
  return (
    <div className="flex flex-col gap-4 p-1">
      <CompanyDisplayInputs />
      <Tabs defaultValue="general">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="palm-oil">Palm Oil</TabsTrigger>
          <TabsTrigger value="entities">Entities</TabsTrigger>
          <TabsTrigger value="enrichment">Enrichment</TabsTrigger>
        </TabsList>
        <TabsContent value="general" className="pt-4">
          <GeneralTab />
        </TabsContent>
        <TabsContent value="palm-oil" className="pt-4">
          <PalmOilTab />
        </TabsContent>
        <TabsContent value="entities" className="pt-4">
          <EntitiesTab />
        </TabsContent>
        <TabsContent value="enrichment" className="pt-4">
          <EnrichmentTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const CompanyDisplayInputs = () => {
  const record = useRecordContext<Company>();
  return (
    <div className="flex gap-4 flex-1 flex-row">
      <ImageEditorField
        source="logo"
        type="avatar"
        width={60}
        height={60}
        emptyText={record?.name.charAt(0)}
        linkPosition="bottom"
      />
      <TextInput
        source="name"
        className="w-full h-fit"
        validate={required()}
        helperText={false}
        placeholder="Company name"
      />
    </div>
  );
};

const GeneralTab = () => {
  const isMobile = useIsMobile();

  return (
    <div className={`flex gap-6 ${isMobile ? "flex-col" : "flex-row"}`}>
      <div className="flex flex-col gap-6 flex-1">
        <div className="flex flex-col gap-4">
          <h6 className="text-lg font-semibold">Contact</h6>
          <TextInput source="website" helperText={false} validate={isUrl} />
          <TextInput
            source="linkedin_url"
            helperText={false}
            validate={isLinkedinUrl}
          />
          <TextInput source="phone_number" helperText={false} />
        </div>
        <div className="flex flex-col gap-4">
          <h6 className="text-lg font-semibold">Context</h6>
          <ReferenceInput source="channel_id" reference="channels">
            <SelectInput
              label="Sales Channel"
              optionText="name"
              helperText={false}
            />
          </ReferenceInput>
          <TextInput source="revenue" helperText={false} />
          <TextInput source="tax_identifier" helperText={false} />
        </div>
      </div>
      <div className="flex flex-col gap-6 flex-1">
        <div className="flex flex-col gap-4">
          <h6 className="text-lg font-semibold">Address</h6>
          <TextInput source="address" helperText={false} />
          <TextInput source="city" helperText={false} />
          <TextInput source="zipcode" helperText={false} />
          <SelectInput
            source="state_abbr"
            label="State"
            choices={malaysianStates}
            helperText={false}
          />
          <TextInput source="country" helperText={false} />
        </div>
        <div className="flex flex-col gap-4">
          <h6 className="text-lg font-semibold">Additional information</h6>
          <TextInput source="description" multiline helperText={false} />
          <ArrayInput source="context_links" helperText={false}>
            <SimpleFormIterator
              disableReordering
              fullWidth
              getItemLabel={false}
            >
              <TextInput
                source=""
                label={false}
                helperText={false}
                validate={isUrl}
              />
            </SimpleFormIterator>
          </ArrayInput>
        </div>
        <div className="flex flex-col gap-4">
          <h6 className="text-lg font-semibold">Account manager</h6>
          <ReferenceInput
            source="sales_id"
            reference="sales"
            filter={{ "disabled@neq": true }}
          >
            <SelectInput
              label="Account manager"
              helperText={false}
              optionText={saleOptionRenderer}
            />
          </ReferenceInput>
        </div>
      </div>
    </div>
  );
};

const PalmOilTab = () => {
  const isMobile = useIsMobile();

  return (
    <div className={`flex gap-6 ${isMobile ? "flex-col" : "flex-row"}`}>
      <div className="flex flex-col gap-4 flex-1">
        <SelectInput
          source="size"
          label="Size (Hectarage)"
          choices={sizes}
          helperText={false}
        />
        <SelectInput
          source="entity_type"
          label="Entity Type"
          choices={entityTypeChoices}
          helperText={false}
        />
      </div>
      <div className="flex flex-col gap-4 flex-1">
        <SelectInput
          source="state_abbr"
          label="State"
          choices={malaysianStates}
          helperText={false}
        />
        <TextInput
          source="total_planted_ha"
          label="Total Planted (Ha)"
          helperText={false}
        />
        <TextInput source="corporate_group" helperText={false} />
        <TextInput
          source="parent_company_name"
          label="Parent Company"
          helperText={false}
        />
        <TextInput
          source="group_company"
          label="Group Company"
          helperText={false}
        />
      </div>
    </div>
  );
};

const EntitiesTab = () => {
  const record = useRecordContext<Company>();
  const { data: entities } = useGetList<Entity>("entities", {
    filter: { company_id: record?.id },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  }, { enabled: !!record?.id });

  if (!entities?.length) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        No entities linked to this company.
      </p>
    );
  }

  return (
    <div className="border rounded-md divide-y max-h-80 overflow-y-auto">
      {entities.map((e) => (
        <div key={String(e.id)} className="px-3 py-2 text-sm">
          <div className="font-medium">{e.name}</div>
          <div className="text-xs text-muted-foreground">
            {e.planted_area_ha
              ? `${e.planted_area_ha.toLocaleString()} ha`
              : "—"}
            {e.state ? ` · ${e.state}` : ""}
            {e.track_id ? ` · ${e.track_id}` : ""}
          </div>
        </div>
      ))}
    </div>
  );
};

const EnrichmentTab = () => {
  // Available for future conditional rendering (e.g. hide fields based on intel_status)
  const _record = useRecordContext<Company>();
  return (
    <div className="flex flex-col gap-4 max-w-md">
      <SelectInput
        source="intel_status"
        label="Intel Status"
        choices={intelStatusChoices}
        helperText={false}
      />
      <TextInput
        source="contacts_enriched_count"
        label="Contacts Enriched"
        helperText={false}
        disabled
      />
      <TextInput
        source="last_enrichment_date"
        label="Last Enrichment Date"
        helperText={false}
        disabled
      />
      <TextInput
        source="enrichment_sources"
        label="Enrichment Sources"
        helperText={false}
        disabled
      />
      <DateInput
        source="last_cold_call_list_date"
        label="Last Cold Call List Date"
        helperText={false}
      />
      <DateInput
        source="last_contact_attempt"
        label="Last Contact Attempt"
        helperText={false}
      />
    </div>
  );
};

const saleOptionRenderer = (choice: Sale) =>
  `${choice.first_name} ${choice.last_name}`;

const entityTypeChoices = [
  { id: "Private Limited", name: "Private Limited" },
  { id: "Public Limited", name: "Public Limited" },
  { id: "Unidentified", name: "Unidentified" },
];

const intelStatusChoices = [
  { id: "QUEUED", name: "Queued" },
  { id: "PROCESSING", name: "Processing" },
  { id: "COMPLETED", name: "Completed" },
  { id: "ERROR", name: "Error" },
  { id: "NOT_FOUND", name: "Not Found" },
];

const malaysianStates = [
  { id: "Johor", name: "Johor" },
  { id: "Kedah", name: "Kedah" },
  { id: "Kelantan", name: "Kelantan" },
  { id: "Melaka", name: "Melaka" },
  { id: "Negeri Sembilan", name: "Negeri Sembilan" },
  { id: "Pahang", name: "Pahang" },
  { id: "Perak", name: "Perak" },
  { id: "Perlis", name: "Perlis" },
  { id: "Pulau Pinang", name: "Pulau Pinang" },
  { id: "Sabah", name: "Sabah" },
  { id: "Sarawak", name: "Sarawak" },
  { id: "Selangor", name: "Selangor" },
  { id: "Terengganu", name: "Terengganu" },
  { id: "WP Kuala Lumpur", name: "WP Kuala Lumpur" },
  { id: "WP Labuan", name: "WP Labuan" },
];
